import React, { useRef, useState } from 'react';
import { Box, Card, Stack, Typography, IconButton, Button, Chip } from '@mui/material';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { IAudition } from '../../types/global/helper';
import { CustomChipGroup } from '../Chip/CustomChipGroup';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { TextWithIcon } from '../TextWithIcon/TextWithIcon';
import GpsFixedOutlined from '@mui/icons-material/GpsFixedOutlined';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import { RectangularImageCropperModal } from '../ImageCropper/RectangularImageCropperModal';

export interface ICoverImageRenderer {
    audition: IAudition;
    editMode?: boolean;
    onImageChange?: (image: string, rawFile: File) => void;
}

const CoverImageRenderer: React.FC<ICoverImageRenderer> = ({ audition, editMode = false, onImageChange }) => {
    const { themeStyle } = useThemeContext();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [src, setSrc] = useState<string>('');
    const [pendingImage, setPendingImage] = useState<string | null>(null);
    const [pendingFile, setPendingFile] = useState<File | null>(null);

    const handleUploadClick = () => {
        if (fileInputRef.current) fileInputRef.current.click();
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            const file = files[0];
            setSrc(URL.createObjectURL(file));
            setModalOpen(true);
        }
    };

    // After cropping, show preview and buttons, but do not call onImageChange yet
    const handleCropSave = (base64: string, file: File) => {
        setModalOpen(false);
        setSrc('');
        setPendingImage(base64);
        setPendingFile(file);
    };

    const handleCropCancel = () => {
        setModalOpen(false);
        setSrc('');
    };

    // User clicks Cancel on preview overlay
    const handlePendingCancel = () => {
        setPendingImage(null);
        setPendingFile(null);
    };

    // User clicks Save on preview overlay
    const handlePendingSave = () => {
        if (pendingImage && pendingFile && onImageChange) {
            onImageChange(pendingImage, pendingFile);
        }
        setPendingImage(null);
        setPendingFile(null);
    };

    // Determine which image to show
    const displayImage = pendingImage || audition?.imageCover;

    return (
        <Card
            sx={{
                width: '100%',
                borderRadius: 2,
                overflow: 'hidden',
                objectFit: 'cover',
                position: 'relative',
                my: 2,
                height: {
                    xs: '300px',
                },
                backgroundColor: displayImage ? 'transparent' : audition.background_color,
            }}>
            <Box
                id="featured-contest"
                style={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'flex-start',
                    position: 'relative',
                    borderRadius: 10,
                }}
                sx={{
                    background: displayImage
                        ? `linear-gradient(to top, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.3)), url(${displayImage})`
                        : audition.background_color,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    height: '100%',
                    width: '100%',
                    overflow: 'hidden',
                }}>
                {/* Upload Icon Overlay */}
                {editMode && !pendingImage && (
                    <IconButton
                        sx={{
                            position: 'absolute',
                            bottom: 15,
                            right: 15,
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            color: theme[themeStyle].primary_white,
                            zIndex: 10,
                            '&:hover': { backgroundColor: theme[themeStyle].primary_orange },
                        }}
                        onClick={handleUploadClick}
                        aria-label="Upload Cover Image">
                        <PhotoCamera sx={{ color: theme[themeStyle].true_white }} />
                        <input
                            type="file"
                            accept="image/*"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            onChange={handleFileChange}
                        />
                    </IconButton>
                )}
                {/* Reusable Cropper Modal */}
                <RectangularImageCropperModal
                    open={modalOpen}
                    src={src}
                    onSave={handleCropSave}
                    onCancel={handleCropCancel}
                    aspect={1.4}
                    cropHeight={300}
                />
                {/* Pending image preview overlay with Save/Cancel */}
                {editMode && pendingImage && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            zIndex: 20,
                            background: 'rgba(0,0,0,0.25)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                        <img
                            src={pendingImage}
                            alt="Preview"
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                borderRadius: 8,
                                boxShadow: '0 2px 16px rgba(0,0,0,0.2)',
                            }}
                        />
                        <Box sx={{ position: 'absolute', top: 20, right: 20, display: 'flex', gap: 2 }}>
                            <Button variant="outlined" color="inherit" onClick={handlePendingCancel} sx={{ mr: 1 }}>
                                Cancel
                            </Button>
                            <Button variant="contained" color="primary" onClick={handlePendingSave}>
                                Save
                            </Button>
                        </Box>
                    </Box>
                )}
                <Box sx={{ position: 'absolute', left: 15, top: 15 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        {audition.union_affiliation && (
                            <Chip
                                label={audition.union_affiliation}
                                size="small"
                                sx={{
                                    backgroundColor: '#000',
                                    color: theme[themeStyle].true_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    textTransform: 'uppercase',
                                    fontSize: 10,
                                    height: '24px',
                                    borderRadius: '6px',
                                }}
                            />
                        )}
                        <CustomChipGroup tags={audition.tags} clickable={false} withNiceColor />
                    </Stack>
                </Box>
                <Box sx={{ position: 'absolute', right: 15, top: 15 }}>
                    <CustomChipGroup tags={[audition.gig_type]} clickable={false} withNiceColor />
                </Box>
                <Stack position="absolute" bottom={'20%'} left={15}>
                    <TextWithIcon
                        icon={<GpsFixedOutlined sx={{ fontSize: '12', color: theme[themeStyle].true_white }} />}
                        text={audition?.shoot_location?.address ? audition?.shoot_location?.address : 'Remote'}
                        typographyProps={{
                            sx: {
                                fontSize: 10,
                                color: theme[themeStyle].true_white,
                            },
                        }}
                    />
                    <Typography
                        style={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 32, color: theme[themeStyle].true_white }}>
                        {audition.title}
                    </Typography>
                    <CustomChipGroup
                        tags={audition.required_media}
                        tagSx={{
                            color: theme[themeStyle].true_white,
                        }}
                    />
                </Stack>
            </Box>
        </Card>
    );
};

export { CoverImageRenderer };
